import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "p-2 p-lg-0" }
const _hoisted_2 = { class: "title-2 text-primary-black" }
const _hoisted_3 = { class: "caption mb-4" }
const _hoisted_4 = { class: "mb-4 d-flex justify-content-end" }

import { ref, Ref,  computed, ComputedRef } from 'vue';
import { useI18n } from 'vue-i18n';
import {  useStore } from 'vuex';

// Component imports
import PasswordFields from './PasswordFields.vue';
import Button from '@/components/UI/Button.vue';

// Types
import { FormValues, UpdateFn, ShowPasswords, ValidationRules, validatePasswordLength } from './EditPassword';

// Composables
import { useValidation } from '@/composables/useValidation';
import InputFieldError from '@/components/UI/InputFieldError.vue';
import { ModalId } from '@/config/modalIds';

/**
 * Localization and State Management
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'EditPasswordForm',
  setup(__props) {

/**
 * Imports and Dependencies
 */
// Vue and third-party imports
const { t } = useI18n(); // Translation function
const store = useStore(); // Vuex store

// Store values


/**
 * Reactive State
 */
const formValues: Ref<FormValues> = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})


const showPasswords: Ref<ShowPasswords> = ref({
  current: false,
  new: false,
  confirm: false,
});

const isSubmitting = ref<boolean>(false);
const submitted: Ref<boolean> = ref(false)

// Validation
const validationRules: ValidationRules = {
  currentPassword: (value: string) => validatePasswordLength(value),
  newPassword: (value: string) => validatePasswordLength(value),
  confirmPassword: (value: string) => {
    const lengthCondition: boolean = validatePasswordLength(value)
    const passwordsEqual = formValues.value.newPassword === value
    return lengthCondition && passwordsEqual
  },
}
const {invalidFields} = useValidation(formValues, validationRules)

// Computed
const hasErrors: ComputedRef<boolean> = computed(() => {
  return invalidFields.value.size !== 0
})

const showErrors: ComputedRef<boolean> = computed(() => {
  return hasErrors.value && submitted.value
})

const isLoading: ComputedRef<boolean> = computed(() => {
    const isLoadingGetter = store.getters['auth/isLoading']
    return isLoadingGetter('changePassword')
  })

const updateError: ComputedRef<string | null> = computed(() => {
  const errorGetter = store.getters['auth/getError']
  return errorGetter('changePassword')
})



/**
 * Toggle password visibility for a specific field
 * @param field - The password field to toggle (current, new, or confirm)
 */
const togglePasswordVisibility = (field: keyof ShowPasswords): void => {
  showPasswords.value[field] = !showPasswords.value[field];
};

// Update function to pass to subforms
const updateFormValues: UpdateFn = (key, value) => {
    formValues.value[key] = value
}

/**
 * Handle password reset submission
 */
const handlePasswordReset = async (): Promise<void> => {
  submitted.value = true
  
  // Do not send the request if we have errors
  if(hasErrors.value) return

  try {
    isSubmitting.value = true;

    // Prepare payload and headers
    const payload = {
      oldPassword: formValues.value.currentPassword,
      password: formValues.value.newPassword,
    };

    // Dispatch password reset action
    await store.dispatch('auth/changePassword', { payload });
     store.commit('modal/close', ModalId.EDIT_INFO_PANEL);
  } catch (error) {
    console.error(error)
  } finally {
    isSubmitting.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    _createElementVNode("h2", _hoisted_2, _toDisplayString(_unref(t)('profile.resetPassword')), 1),
    _createElementVNode("p", _hoisted_3, _toDisplayString(_unref(t)('profile.enterYourPassword')), 1),
    _createVNode(PasswordFields, {
      "form-values": formValues.value,
      "update-fn": updateFormValues,
      "show-errors": showErrors.value,
      showPasswords: showPasswords.value,
      "invalid-fields": _unref(invalidFields),
      onTogglePasswordVisibility: togglePasswordVisibility
    }, null, 8, ["form-values", "show-errors", "showPasswords", "invalid-fields"]),
    (updateError.value)
      ? (_openBlock(), _createBlock(InputFieldError, {
          key: 0,
          text: _unref(t)('errorMessages.changePassword')
        }, null, 8, ["text"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(Button, {
        id: 'saveButton',
        class: "w-50",
        "aria-label": _unref(t)('profile.savePassword'),
        onButtonClick: handlePasswordReset,
        disabled: showErrors.value || isLoading.value,
        loading: isLoading.value
      }, {
        text: _withCtx(() => [
          _createTextVNode(_toDisplayString(isSubmitting.value
              ? _unref(t)('profile.savingPassword')
              : _unref(t)('profile.savePassword')), 1)
        ]),
        _: 1
      }, 8, ["aria-label", "disabled", "loading"])
    ])
  ]))
}
}

})